// AgentX chat (Plans/agentic_trader_v2_plan.html). Superusers only while the server switch is on.
// Conversations are stored per user on the server; the history lists them, newest first, like Claude / ChatGPT.
// Two shapes: variant="panel" (the floating chat box — history behind a ☰) and variant="page" (/agentx — history as
// a sidebar on wide screens).
// SAFETY: answers are rendered as TEXT — never HTML, never images (an image URL is the classic silent leak of data a
// prompt injection put into it); only http(s) links render, and they open in a new tab with no referrer.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import agentxService from '../../services/agentxService';
import LaunchCard from './LaunchCard';

const POLL_MS = 2000;
const RUNNING = ['queued', 'running'];

const money = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n < 0.01 && n > 0 ? `$${n.toFixed(4)}` : `$${n.toFixed(2)}`;
};

const when = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const today = new Date();
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { day: 'numeric', month: 'short' });
};

// ── text-only rendering: paragraphs, bullet / numbered lists, **bold**, `code`, http(s) links ─────────────────────
const LINK_RE = /(https?:\/\/[^\s)<>"']+)/g;

function Inline({ text }) {
  const parts = [];
  String(text).split(LINK_RE).forEach((chunk, i) => {
    if (i % 2 === 1) {
      parts.push(
        <a key={`l${i}`} href={chunk} target="_blank" rel="noopener noreferrer nofollow"
           className="text-brand-gold underline underline-offset-2 break-all">{chunk}</a>,
      );
      return;
    }
    chunk.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).forEach((p, j) => {
      if (!p) return;
      if (p.startsWith('**') && p.endsWith('**')) parts.push(<strong key={`b${i}-${j}`}>{p.slice(2, -2)}</strong>);
      else if (p.startsWith('`') && p.endsWith('`')) {
        parts.push(<code key={`c${i}-${j}`} className="px-1 rounded bg-gray-100 dark:bg-gray-800 text-[0.85em]">{p.slice(1, -1)}</code>);
      } else parts.push(<React.Fragment key={`t${i}-${j}`}>{p}</React.Fragment>);
    });
  });
  return <>{parts}</>;
}

function AnswerText({ text }) {
  const blocks = useMemo(() => {
    const out = [];
    let list = null;
    String(text || '').split('\n').forEach((raw) => {
      const line = raw.replace(/\s+$/, '');
      const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
      const num = line.match(/^\s*\d+[.)]\s+(.*)$/);
      const heading = line.match(/^\s*#{1,4}\s+(.*)$/);
      if (bullet || num) {
        const kind = bullet ? 'ul' : 'ol';
        if (!list || list.kind !== kind) { list = { kind, items: [] }; out.push(list); }
        list.items.push((bullet || num)[1]);
        return;
      }
      list = null;
      if (!line.trim()) { out.push({ kind: 'gap' }); return; }
      out.push(heading ? { kind: 'h', text: heading[1] } : { kind: 'p', text: line });
    });
    return out;
  }, [text]);
  return (
    <div className="text-sm leading-relaxed text-gray-800 dark:text-gray-200 space-y-1.5 break-words">
      {blocks.map((b, i) => {
        if (b.kind === 'gap') return <div key={i} className="h-1" />;
        if (b.kind === 'h') return <p key={i} className="font-semibold text-gray-900 dark:text-gray-100 pt-1"><Inline text={b.text} /></p>;
        if (b.kind === 'p') return <p key={i}><Inline text={b.text} /></p>;
        const Tag = b.kind;
        return (
          <Tag key={i} className={`${b.kind === 'ul' ? 'list-disc' : 'list-decimal'} pl-5 space-y-0.5`}>
            {b.items.map((it, j) => <li key={j}><Inline text={it} /></li>)}
          </Tag>
        );
      })}
    </div>
  );
}

const STATUS_CHIP = {
  ok: 'text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
  error: 'text-amber-600 dark:text-amber-400 border-amber-500/30',
  refused: 'text-gray-500 border-gray-400/40',
  blocked: 'text-red-600 dark:text-red-400 border-red-500/40',
};

const TOOL_LABEL = {
  my_access: 'Checked what you can see',
  web_search: 'Searched the web',
  web_fetch: 'Read a web page',
  list_strategies: 'Looked up the strategies',
  strategy_evidence: 'Read the tested evidence',
  live_track_record: 'Read your live track record',
  sentiment: 'Read the research sentiment',
  company_profile: 'Read the company profile',
  price_and_quality: 'Checked the data quality',
  check_readiness: 'Checked readiness to optimize',
  optimization_card: 'Prepared a launch card',
  optimization_status: 'Checked a run',
  symbol_onboarding: 'Onboarded a new name',
};

function ToolSteps({ tools }) {
  const [open, setOpen] = useState(false);
  if (!tools || !tools.length) return null;
  return (
    <div className="mt-2">
      <button type="button" onClick={() => setOpen((o) => !o)}
              className="text-[11px] uppercase tracking-wider text-gray-500 dark:text-gray-400 hover:text-brand-gold">
        {open ? '▾' : '▸'} {tools.length} step{tools.length > 1 ? 's' : ''}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {tools.map((t, i) => (
            <li key={i} className="text-xs text-gray-600 dark:text-gray-400 flex flex-wrap items-center gap-1.5">
              <span className={`px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wide ${STATUS_CHIP[t.status] || ''}`}>
                {t.status}
              </span>
              <span>{TOOL_LABEL[t.name] || t.name}</span>
              {t.args && Object.keys(t.args).length > 0 && (
                <span className="text-gray-400 dark:text-gray-500 break-all">
                  {Object.entries(t.args).map(([k, v]) => `${k}: ${v}`).join(' · ')}
                </span>
              )}
              <span className="text-gray-400 dark:text-gray-600 tabular-nums">{(t.duration_ms / 1000).toFixed(1)}s</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function Turn({ turn }) {
  const running = RUNNING.includes(turn.status);
  return (
    <div className="space-y-2">
      <div className="flex justify-end">
        <div className="max-w-[85%] rounded-2xl rounded-br-sm px-3.5 py-2 bg-brand-gold/10 border border-brand-gold/30
                        text-sm text-gray-900 dark:text-gray-100 whitespace-pre-wrap break-words">
          {turn.question}
        </div>
      </div>
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-[#0d1117] px-4 py-3">
        {running && !turn.answer && (
          <p className="text-sm text-gray-500 dark:text-gray-400 animate-pulse">
            {turn.status === 'queued' ? 'Waiting for the agent…' : `Working… step ${turn.steps || 1}`}
          </p>
        )}
        {turn.answer && <AnswerText text={turn.answer} />}
        {running && turn.answer && <p className="mt-2 text-xs text-gray-400 animate-pulse">still working…</p>}
        {(turn.tools || []).filter((t) => t.name === 'optimization_card' && t.data?.card?.ticker && t.data.card.strategy)
          .slice(-1).map((t, i) => <LaunchCard key={`${turn.id}-card-${i}`} initial={t.data.card} />)}
        <ToolSteps tools={turn.tools} />
        {!running && (
          <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-400 dark:text-gray-500 tabular-nums">
            <span>{money(turn.cost_usd)}</span>
            {turn.models && turn.models.length > 0 && <span>{turn.models.join(', ')}</span>}
            {turn.fallbacks && turn.fallbacks.length > 0 && <span className="text-amber-500">fell back from {turn.fallbacks.map((f) => f.model).join(', ')}</span>}
            {turn.status !== 'done' && <span className="text-amber-500">{turn.status}{turn.stop_reason ? ` · ${turn.stop_reason.replace(/_/g, ' ')}` : ''}</span>}
          </div>
        )}
      </div>
    </div>
  );
}

export default function AgentXChat({ variant = 'page', onClose, onExpand, initialSessionId = null }) {
  const panel = variant === 'panel';
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(initialSessionId);
  const [session, setSession] = useState(null);
  const [spend, setSpend] = useState(null);
  const [text, setText] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [drawer, setDrawer] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  const loadSessions = useCallback(() => agentxService.listSessions()
    .then((r) => setSessions(r.data.sessions || [])).catch(() => {}), []);
  const loadSpend = useCallback(() => agentxService.getSpend()
    .then((r) => setSpend(r.data)).catch(() => {}), []);
  const loadSession = useCallback((id) => {
    if (!id) { setSession(null); return Promise.resolve(); }
    return agentxService.getSession(id).then((r) => setSession(r.data))
      .catch(() => setError('Could not open that conversation.'));
  }, []);

  useEffect(() => { loadSessions(); loadSpend(); }, [loadSessions, loadSpend]);
  useEffect(() => { loadSession(activeId); }, [activeId, loadSession]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }); }, [session]);

  // Poll while the newest answer is still running; refresh the list + spend when it ends.
  const runningTurn = session?.turns?.find((t) => RUNNING.includes(t.status));
  useEffect(() => {
    clearTimeout(pollRef.current);
    if (!runningTurn) return undefined;
    pollRef.current = setTimeout(() => {
      agentxService.getTurn(runningTurn.id).then((r) => {
        setSession((s) => (s ? { ...s, turns: s.turns.map((t) => (t.id === r.data.id ? r.data : t)) } : s));
        if (!RUNNING.includes(r.data.status)) {      // answer finished → title, cost and history refresh
          loadSessions(); loadSpend(); loadSession(activeId);
        }
      }).catch(() => {});
    }, POLL_MS);
    return () => clearTimeout(pollRef.current);
  }, [runningTurn, loadSessions, loadSpend, loadSession, activeId]);

  const send = async () => {
    const q = text.trim();
    if (!q || sending || runningTurn) return;
    setSending(true); setError('');
    try {
      let id = activeId;
      if (!id) {
        const r = await agentxService.createSession();
        id = r.data.id;
        setActiveId(id);
      }
      const r = await agentxService.ask(id, q);
      setText('');
      const optimistic = { id: r.data.turn_id, seq: r.data.seq, status: 'queued', question: q, answer: '', tools: [] };
      setSession((s) => (s && s.id === id ? { ...s, title: s.title || q.slice(0, 120), turns: [...(s.turns || []), optimistic] }
        : { id, title: q.slice(0, 120), turns: [optimistic] }));
      loadSessions();
      loadSession(id);
    } catch (e) {
      setError(e?.response?.data?.error || 'The agent could not take the question. Try again.');
    } finally {
      setSending(false);
    }
  };

  const newChat = () => { setActiveId(null); setSession(null); setError(''); setDrawer(false); };
  const open = (id) => { setActiveId(id); setError(''); setDrawer(false); };

  const sidebar = (
    <div className="flex flex-col h-full">
      <button type="button" onClick={newChat}
              className="m-3 px-3 py-2 rounded-lg border border-brand-gold/40 text-sm text-brand-gold hover:bg-brand-gold/10">
        + New chat
      </button>
      <div className="px-3 pb-1 text-[10px] uppercase tracking-wider text-gray-400">Conversations</div>
      <div className="flex-1 overflow-y-auto scrollbar-thin px-2 pb-3 space-y-0.5">
        {sessions.length === 0 && <p className="px-2 py-3 text-xs text-gray-400">No conversations yet.</p>}
        {sessions.map((s) => (
          <button key={s.id} type="button" onClick={() => open(s.id)}
                  className={`w-full text-left px-2.5 py-2 rounded-lg transition-colors ${
                    s.id === activeId ? 'bg-brand-gold/10 text-gray-900 dark:text-gray-100'
                      : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800/60'}`}>
            <div className="text-[13px] truncate">{s.title || 'Untitled'}</div>
            <div className="text-[10px] text-gray-400 tabular-nums">{when(s.updated_at)} · {money(s.cost_usd)}</div>
          </button>
        ))}
      </div>
      {spend && (
        <div className="border-t border-gray-200 dark:border-gray-800 px-3 py-2 text-[10px] text-gray-400 tabular-nums">
          Today {money(spend.today_usd)}{spend.day_cap_usd ? ` of ${money(spend.day_cap_usd)}` : ''}
        </div>
      )}
    </div>
  );

  return (
    <div className={`relative flex overflow-hidden bg-gray-50 dark:bg-[#0a0d12] ${panel ? 'h-full'
      : 'h-[calc(100vh-9rem)] min-h-[480px] rounded-xl border border-gray-200 dark:border-gray-800'}`}>
      {/* history as a sidebar — the full page on wide screens only */}
      {!panel && <aside className="hidden md:block w-64 shrink-0 border-r border-gray-200 dark:border-gray-800">{sidebar}</aside>}
      {/* history as a drawer — the chat box, and the page on narrow screens */}
      {drawer && (
        <div className={`${panel ? 'absolute' : 'md:hidden fixed'} inset-0 z-40 flex`}>
          <div className="w-72 max-w-[85%] bg-gray-50 dark:bg-[#0a0d12] border-r border-gray-200 dark:border-gray-800">{sidebar}</div>
          <button type="button" aria-label="Close conversations" className="flex-1 bg-black/40" onClick={() => setDrawer(false)} />
        </div>
      )}

      <section className="flex-1 min-w-0 flex flex-col">
        <header className="flex items-center gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <button type="button" className={`${panel ? '' : 'md:hidden'} text-gray-500 hover:text-brand-gold`}
                  onClick={() => setDrawer(true)} aria-label="Conversations" title="Conversations">☰</button>
          <h2 className="font-serif text-base text-gray-900 dark:text-gray-100 truncate">
            {session?.title || 'AgentX'}
          </h2>
          <div className="ml-auto flex items-center gap-3">
            {session && <span className="text-[11px] text-gray-400 tabular-nums">{money(session.cost_usd)}</span>}
            {panel && (
              <button type="button" onClick={newChat} className="text-gray-500 hover:text-brand-gold text-sm"
                      title="New chat" aria-label="New chat">＋</button>
            )}
            {onExpand && (
              <button type="button" onClick={() => onExpand(activeId)} className="text-gray-500 hover:text-brand-gold text-sm"
                      title="Open full page" aria-label="Open full page">⤢</button>
            )}
            {onClose && (
              <button type="button" onClick={onClose} className="text-gray-500 hover:text-brand-gold text-sm"
                      title="Close" aria-label="Close AgentX">✕</button>
            )}
          </div>
        </header>

        <div className="flex-1 overflow-y-auto scrollbar-thin px-4 py-4 space-y-5">
          {!session && (
            <div className={`max-w-lg mx-auto text-center ${panel ? 'pt-6' : 'pt-10'}`}>
              <p className="font-serif text-lg text-gray-900 dark:text-gray-100">Ask AgentX about a stock, a strategy or your sleeves.</p>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                The agent reads the platform's tested strategies, your track record and the research, and searches the web.
                It cannot change anything, deploy a strategy or place an order. Evidence, not financial advice.
              </p>
            </div>
          )}
          {session?.turns?.map((t) => <Turn key={t.id} turn={t} />)}
          <div ref={bottomRef} />
        </div>

        <div className="border-t border-gray-200 dark:border-gray-800 p-3">
          {error && <p className="mb-2 text-xs text-red-500">{error}</p>}
          <div className="flex gap-2 items-end">
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
              rows={2}
              maxLength={4000}
              placeholder={runningTurn ? 'The agent is answering…' : 'e.g. What is the best way to invest in Nvidia?'}
              disabled={!!runningTurn}
              className="flex-1 resize-none rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-[#0d1117]
                         px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:border-brand-gold"
            />
            <button type="button" onClick={send} disabled={sending || !!runningTurn || !text.trim()}
                    className="px-4 py-2 rounded-lg bg-brand-gold text-black text-sm font-medium disabled:opacity-40">
              Send
            </button>
          </div>
        </div>
      </section>
    </div>
  );
}
